import React, { useEffect, useMemo, useState } from 'react';
import { MonitorSmartphone, LogOut, FolderOpen, RefreshCw, ShieldCheck, AlertTriangle, Pencil, ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import { doc, onSnapshot, addDoc, collection, updateDoc, arrayUnion, arrayRemove, query, where, deleteField, FieldPath } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PageHeader from '../common/PageHeader';
import OpenAgentSettingsButton from '../common/OpenAgentSettingsButton';
import { isMissingStmtFolder, MISSING_STMT_TITLE, MISSING_STMT_HELP } from '../../utils/agentFolders';

// 접속 PC 관리 (9월 묶음 #3 — 협조적 차단 1단계, 2026-08-06 대표 승인)
// - agentStatus/{labId}.devices 맵을 구독해 이 계정으로 로그인된 덴클 프로그램 PC 목록 표시
// - [이 PC 로그아웃] = agentCommands에 type 'remote_logout' 명령 생성 (웹은 명령 생성까지 —
//   실제 로그아웃 처리는 에이전트가 pending 명령을 집어 수행. 꺼진 PC는 다음 실행 시 적용)
// - 대표 전용: /settings 하위라 기공소 대표 PIN 구역(OWNER_ZONE_PREFIXES)에 이미 포함됨.
//   직원 계정은 아래에서 한 번 더 차단.
// - 다중 PC 2단계 — 신규 PC 승인제 (2026-08-06 대표 확정: 무제한 + 5대 초과 시 승인):
//   에이전트 계약: agentStatus/{labId}.approvedDeviceIds(string[], 웹이 관리·에이전트는 읽기만),
//   미승인 6대째 PC는 하트비트 devices 맵에 pendingApproval:true를 게시.
//   웹은 [승인](arrayUnion) / [승인 해제](arrayRemove)만 수행. 활성 PC 5대 이하면 승인 UI 미노출.

const ONLINE_MIN = 10;   // 🟢 켜짐 판정 기준 (분) — 하트비트 5분 주기 × 2 (2026-09-06 대표 신고 정비: 15→10)
const ACTIVE_DAYS = 30;  // "활성 PC" 판정 기준 (일) — 최근 30일 내 신호
const FREE_LIMIT = 5;    // 승인 없이 쓸 수 있는 PC 대수
const STALE_DAYS = 7;    // [목록에서 제거] 노출 기준 (일) — 7일 이상 신호 없음
const OLD_ALPHA_MAX = 49; // 구버전 판정 — 1.0.0-alpha.49 이하 (현행 alpha.5x). 0.9.x 구계열도 구버전

// 구버전 판정 — "0.9.46"(구계열) / "1.0.0-alpha.14"(alpha.49 이하) → true. 형식을 모르면 false(제거 유도 안 함)
function isOldVersion(v) {
    if (!v || typeof v !== 'string') return false;
    if (/^0\./.test(v)) return true;
    const m = v.match(/alpha\.(\d+)/);
    if (m) return Number(m[1]) <= OLD_ALPHA_MAX;
    return false;
}

function toMs(t) {
    if (!t) return null;
    if (typeof t.toMillis === 'function') return t.toMillis();
    const d = new Date(t);
    return isNaN(d) ? null : d.getTime();
}

function agoText(ms) {
    if (!ms) return '기록 없음';
    const min = Math.floor((Date.now() - ms) / 60000);
    if (min < 1) return '방금 전';
    if (min < 60) return `${min}분 전`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}시간 전`;
    return `${Math.floor(h / 24)}일 전`;
}

// ⚠️ 느린 폴더 경고 (다리 3종 ③, 2026-08-20) — 하트비트 devices.{id}.diag.slowFolder / diag.cycleInterval
// cycleInterval 단위 = ms (alpha.38 계약 확정, 2026-08-20)
function cycleMinutes(v) {
    const n = Number(v);
    if (!isFinite(n) || n <= 0) return null;
    return Math.max(1, Math.round(n / 60000));
}

// 「최근 활동」 로그 한 줄 시각 표기 — at은 ms 에폭 또는 Timestamp 대응
function logTimeText(at) {
    const ms = typeof at === 'number' ? at : toMs(at);
    if (!ms) return '';
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const EDITION_LABEL = { collector: '수집 전용판', full: '풀버전' };
const PLATFORM_LABEL = { win32: 'Windows', darwin: 'Mac', linux: 'Linux' };

function AgentDevices({ user }) {
    const labId = user?.companyId || user?.uid;
    const isStaff = user?.userType === 'staff';
    const businessType = user?.businessType || user?.companyBusinessType;
    const isLab = businessType === 'lab' || businessType === '기공소';

    const [status, setStatus] = useState(undefined);       // undefined=로딩, null=문서 없음
    const [loadError, setLoadError] = useState(null);
    const [sending, setSending] = useState(null);          // 전송 중인 deviceId
    const [sentIds, setSentIds] = useState([]);            // 명령 보낸 deviceId 목록 (재클릭 방지)
    const [notice, setNotice] = useState(null);            // 상단 안내 문구
    const [logOpenIds, setLogOpenIds] = useState([]);      // 「최근 활동」 펼친 deviceId (다리 3종 ②, 2026-08-20)
    const [pendingLogouts, setPendingLogouts] = useState({}); // deviceId → { id, createdAtMs } 대기 중 로그아웃 명령 (2026-09-06)
    const [cancelling, setCancelling] = useState(null);    // 예약 취소 처리 중인 명령 id
    const [removing, setRemoving] = useState(null);        // [목록에서 제거] 처리 중인 deviceId

    useEffect(() => {
        if (!labId) return;
        const unsub = onSnapshot(doc(db, 'agentStatus', labId), (snap) => {
            setStatus(snap.exists() ? snap.data() : null);
            setLoadError(null);
        }, (e) => {
            console.error('접속 PC 목록 조회 실패:', e);
            setLoadError(e?.message || String(e));
        });
        return () => unsub();
    }, [labId]);

    // ⏳ 대기 중 로그아웃 명령 구독 (2026-09-06 대표 신고 "로그아웃이 잘 안 됨" 정비) —
    //    보낸 명령이 전부 꺼진 PC 대상이라 pending으로만 남아 있던 것을 화면에 드러낸다.
    //    쿼리는 에이전트와 같은 labId+status(복합 인덱스 불필요), type은 클라이언트에서 거른다.
    //    같은 PC에 여러 건이면 가장 최근 것 하나만 표시.
    useEffect(() => {
        if (!labId) return;
        const q = query(collection(db, 'agentCommands'), where('labId', '==', labId), where('status', '==', 'pending'));
        const unsub = onSnapshot(q, (snap) => {
            const next = {};
            snap.docs.forEach(d => {
                const data = d.data();
                if (data.type !== 'remote_logout' || !data.targetDeviceId) return;
                const createdAtMs = toMs(data.createdAt) || 0;
                const prev = next[data.targetDeviceId];
                if (!prev || createdAtMs > prev.createdAtMs) next[data.targetDeviceId] = { id: d.id, createdAtMs };
            });
            setPendingLogouts(next);
        }, (e) => {
            console.error('[접속 PC] 대기 중 로그아웃 명령 조회 실패:', e?.code || e?.message);
        });
        return () => unsub();
    }, [labId]);

    // 「최근 활동」 로그 — agentStatus/{labId}/priv/logs 하위 문서 (계약 변경 2026-08-20:
    // 연결 치과가 agentStatus 본문을 get 할 수 있어 로그는 회사 전용 priv 문서로 분리).
    // 구조: { devices: { {deviceId}: { logs: [{at,m}], updatedAt } } }
    // 문서 없음/권한 오류(rules 배포 전·구버전) = null 유지 → "로그 미지원" 안내로 조용히 동작.
    const [privLogs, setPrivLogs] = useState(null);
    useEffect(() => {
        if (!labId) return;
        const unsub = onSnapshot(doc(db, 'agentStatus', labId, 'priv', 'logs'), (snap) => {
            setPrivLogs(snap.exists() ? snap.data() : null);
        }, (e) => {
            // rules 배포 전에는 permission-denied가 정상 경로 — 화면 오류로 올리지 않고 코드만 기록
            console.log('[접속 PC] 활동 로그 문서 조회 실패(rules 배포 전이면 정상):', e?.code || e?.message);
            setPrivLogs(null);
        });
        return () => unsub();
    }, [labId]);

    const devices = useMemo(() => {
        if (!status) return [];
        const list = Object.values(status.devices || {}).filter(d => d && d.deviceId);
        // 구버전 호환: devices 맵 없이 최상위 lastSeen만 있는 문서 → 1대로 표시 (deviceId 없어 로그아웃 불가)
        if (!list.length && status.lastSeen) {
            list.push({ deviceId: null, deviceName: status.deviceName || '이름 없는 PC (구버전)', lastSeen: status.lastSeen, version: status.version });
        }
        return list
            .map(d => ({ ...d, lastSeenMs: toMs(d.lastSeen), lastCollectMs: toMs(d.lastCollectAt) }))
            .sort((a, b) => (b.lastSeenMs || 0) - (a.lastSeenMs || 0));
    }, [status]);

    // 🖥️ 신규 PC 승인제 파생값 — 활성 PC(최근 30일) / 승인 목록 / 승인 대기 PC
    const approvedIds = useMemo(() => (Array.isArray(status?.approvedDeviceIds) ? status.approvedDeviceIds : []), [status]);
    const activeCount = useMemo(() => devices.filter(d => d.lastSeenMs && (Date.now() - d.lastSeenMs) <= ACTIVE_DAYS * 86400000).length, [devices]);
    const pendingDevices = useMemo(
        () => devices.filter(d => d.pendingApproval && d.deviceId && !approvedIds.includes(d.deviceId)),
        [devices, approvedIds]
    );
    // 승인 UI 노출 조건 — 5대 이하 + 대기·승인 이력도 없으면 현행 목록 그대로 (단순 유지)
    const approvalMode = activeCount > FREE_LIMIT || pendingDevices.length > 0 || approvedIds.length > 0;
    const [approving, setApproving] = useState(null);      // 승인/해제 처리 중인 deviceId

    // [승인] — approvedDeviceIds에 추가 (에이전트가 다음 하트비트 주기에 읽어 수집 재개)
    const approveDevice = async (d) => {
        if (!d.deviceId || approving) return;
        setApproving(d.deviceId);
        try {
            await updateDoc(doc(db, 'agentStatus', labId), { approvedDeviceIds: arrayUnion(d.deviceId) });
            setNotice(`"${d.deviceName || d.deviceId}" PC를 승인했습니다 — 해당 PC의 덴클 프로그램이 곧 정상 동작합니다.`);
        } catch (e) {
            console.error('PC 승인 실패:', e);
            setNotice(`승인에 실패했습니다: ${e?.message || e}`);
        } finally {
            setApproving(null);
        }
    };

    // [승인 해제] — 5대 초과 상태에서 해제하면 해당 PC 수집이 멈춘다는 경고 후 arrayRemove
    const unapproveDevice = async (d) => {
        if (!d.deviceId || approving) return;
        const warn = activeCount > FREE_LIMIT
            ? `"${d.deviceName || d.deviceId}" PC의 승인을 해제할까요?\n\n현재 활성 PC가 ${activeCount}대(${FREE_LIMIT}대 초과)라서, 해제하면 이 PC의 수집·전송이 멈춥니다.\n다시 쓰려면 재승인이 필요합니다.`
            : `"${d.deviceName || d.deviceId}" PC의 승인을 해제할까요?`;
        if (!window.confirm(warn)) return;
        setApproving(d.deviceId);
        try {
            await updateDoc(doc(db, 'agentStatus', labId), { approvedDeviceIds: arrayRemove(d.deviceId) });
            setNotice(`"${d.deviceName || d.deviceId}" PC의 승인을 해제했습니다.`);
        } catch (e) {
            console.error('PC 승인 해제 실패:', e);
            setNotice(`승인 해제에 실패했습니다: ${e?.message || e}`);
        } finally {
            setApproving(null);
        }
    };

    // 🏷️ PC 이름표 (2026-08-17, 기획서_작업현황_반출표시 §4) — agentStatus.deviceNames 맵(웹 전용 필드).
    // 하트비트가 devices.{id}.deviceName을 5분마다 로컬값으로 덮어쓰므로, 웹에서 바꾼 이름은 별도 맵에 둔다.
    // deviceId는 UUID(하이픈 포함)라 점 표기 필드 경로를 못 쓴다 — 맵 전체를 갱신한다.
    const deviceNames = useMemo(() => (status && status.deviceNames) || {}, [status]);
    const displayName = (d) => deviceNames[d.deviceId] || d.deviceName || d.deviceId || '이름 없는 PC';
    const renameDevice = async (d) => {
        if (!d.deviceId) return;
        const input = window.prompt(
            '이 PC의 표시 이름을 입력하세요.\n' +
            '재택 PC는 "박기사(재택)"처럼 사람이 보이는 이름을 권장합니다 — 재택근무·기공 현황에서 누가 가져갔는지 바로 보입니다. (강제 아님)\n' +
            '비우고 확인하면 원래 기기 이름으로 돌아갑니다.',
            deviceNames[d.deviceId] || d.deviceName || ''
        );
        if (input === null) return;
        const name = input.trim().slice(0, 40);
        const next = { ...deviceNames };
        if (name) next[d.deviceId] = name; else delete next[d.deviceId];
        try {
            await updateDoc(doc(db, 'agentStatus', labId), { deviceNames: next });
            setNotice(name ? `PC 이름을 "${name}"(으)로 바꿨습니다 — 재택근무·기공 현황에도 같이 반영됩니다.` : 'PC 이름을 원래 기기 이름으로 되돌렸습니다.');
        } catch (e) {
            console.error('PC 이름 변경 실패:', e);
            setNotice(`이름 변경에 실패했습니다: ${e?.message || e}`);
        }
    };

    // 원격 로그아웃 명령 생성 — 에이전트가 pending 상태의 자기 targetDeviceId 명령을 집어 처리
    // 꺼진 PC(offline=true)면 "예약" — 켜질 때 적용된다는 안내로 확인창을 바꾼다.
    const sendLogout = async (d, offline) => {
        if (!d.deviceId || sending) return;
        const name = displayName(d);
        const ok = window.confirm(offline
            ? `"${name}" PC는 지금 꺼져 있습니다.\n로그아웃을 예약해 두면 이 PC의 덴클 프로그램이 켜질 때 바로 로그아웃됩니다. (24시간 만료 없음)\n\n예약할까요?`
            : `"${name}" PC의 덴클 프로그램을 로그아웃할까요?\n해당 PC에서 다시 로그인하기 전까지 수집·전송이 중단됩니다.`);
        if (!ok) return;
        setSending(d.deviceId);
        try {
            await addDoc(collection(db, 'agentCommands'), {
                labId,
                type: 'remote_logout',
                targetDeviceId: d.deviceId,
                payload: { deviceName: d.deviceName || '' },
                status: 'pending',
                requestedBy: user?.uid || '',
                createdAt: new Date(),
            });
            setSentIds(prev => [...prev, d.deviceId]);
            setNotice(offline
                ? `"${name}" PC에 로그아웃을 예약했습니다 — 지금 꺼져 있어 켜질 때 적용됩니다. 24시간 만료 없음.`
                : `"${name}" PC에 로그아웃 명령을 보냈습니다 — 켜져 있으면 곧 적용됩니다.`);
        } catch (e) {
            console.error('원격 로그아웃 명령 생성 실패:', e);
            setNotice(`명령 전송에 실패했습니다: ${e?.message || e}`);
        } finally {
            setSending(null);
        }
    };

    // [예약 취소] — 대기 중(pending) 명령을 cancelled로 바꾼다. 에이전트는 pending 아닌 명령을 건너뛰므로 실행되지 않는다.
    //   (rules agentCommands update = 회사 접근이면 필드 제한 없음)
    const cancelLogout = async (d, cmd) => {
        if (!cmd || cancelling) return;
        if (!window.confirm(`"${displayName(d)}" PC의 로그아웃 예약을 취소할까요?`)) return;
        setCancelling(cmd.id);
        try {
            await updateDoc(doc(db, 'agentCommands', cmd.id), {
                status: 'cancelled',
                resultMessage: '웹에서 예약 취소',
                completedAt: new Date(),
            });
            setSentIds(prev => prev.filter(id => id !== d.deviceId));
            setNotice(`"${displayName(d)}" PC의 로그아웃 예약을 취소했습니다.`);
        } catch (e) {
            console.error('로그아웃 예약 취소 실패:', e);
            setNotice(`예약 취소에 실패했습니다: ${e?.message || e}`);
        } finally {
            setCancelling(null);
        }
    };

    // [목록에서 제거] — agentStatus.devices.{deviceId}만 지운다(FieldPath: deviceId가 UUID라 점 표기 불가).
    //   목록 정리일 뿐 — approvedDeviceIds·저장된 로그인은 건드리지 않으므로 그 PC가 다시 켜지면 하트비트로 다시 나타난다.
    //   7일 이상 신호 없음 또는 구버전 항목에만 노출 (같은 이름의 최신 항목은 그대로 둔다).
    const removeDevice = async (d) => {
        if (!d.deviceId || removing) return;
        const ok = window.confirm(
            `"${displayName(d)}" 항목을 목록에서 지울까요?\n\n` +
            `목록 정리일 뿐입니다 — 이 PC의 로그인·승인 상태는 그대로이며, 다시 켜지면 목록에 다시 나타납니다.\n` +
            `로그인을 끊으려면 [이 PC 로그아웃]을 쓰세요.`
        );
        if (!ok) return;
        setRemoving(d.deviceId);
        try {
            await updateDoc(doc(db, 'agentStatus', labId), new FieldPath('devices', d.deviceId), deleteField());
            setNotice(`"${displayName(d)}" 항목을 목록에서 지웠습니다. (PC 자격은 그대로 — 다시 켜지면 목록에 다시 표시됩니다)`);
        } catch (e) {
            console.error('PC 목록 제거 실패:', e);
            setNotice(`목록에서 지우지 못했습니다: ${e?.message || e}`);
        } finally {
            setRemoving(null);
        }
    };

    if (isStaff || !isLab) {
        return (
            <div style={S.container}>
                <PageHeader icon={MonitorSmartphone} title="접속 PC 관리" />
                <div style={S.emptyBox}>기공소 대표 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }

    return (
        <div style={S.container}>
            <PageHeader icon={MonitorSmartphone} title="접속 PC 관리" />
            <div style={S.desc}>
                이 계정으로 로그인된 덴클 프로그램(에이전트) PC 목록입니다. 퇴사자 PC 등 더 이상 쓰지 않는
                PC는 [이 PC 로그아웃]으로 연결을 끊을 수 있습니다. 꺼져 있는 PC는 [켜지면 로그아웃(예약)]으로 예약해 두면 켜질 때 적용됩니다.
            </div>

            {/* 승인 대기 PC 알림 — 이 화면 안에서만 강조 (전역 알림 없음) */}
            {pendingDevices.length > 0 && (
                <div style={S.pendingBanner}>
                    <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                        <b>새 PC가 승인을 기다립니다</b> — {pendingDevices.map(d => `"${d.deviceName || d.deviceId}"`).join(', ')} PC는
                        {' '}승인 전까지 수집·전송을 하지 않습니다. 우리 기공소 PC가 맞으면 아래에서 [승인]을 눌러 주세요.
                    </span>
                </div>
            )}

            {devices.length > 0 && (
                <div style={S.countLine}>
                    활성 PC(최근 {ACTIVE_DAYS}일) <b style={{ color: activeCount > FREE_LIMIT ? '#B45309' : '#0f172a' }}>{activeCount}대</b>
                    <span style={{ color: '#94a3b8' }}> · {FREE_LIMIT}대까지는 승인 없이 사용할 수 있고, {FREE_LIMIT + 1}대째부터는 대표 승인이 필요합니다.</span>
                </div>
            )}

            {notice && <div style={S.notice}>{notice}</div>}
            {loadError && <div style={{ ...S.notice, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}>목록을 불러오지 못했습니다: {loadError}</div>}

            {status === undefined && !loadError && <div style={S.emptyBox}>불러오는 중…</div>}

            {status !== undefined && !devices.length && !loadError && (
                <div style={S.emptyBox}>
                    연결된 PC가 없습니다 — 덴클 프로그램을 설치하고 이 계정으로 로그인하면 여기에 표시됩니다.
                </div>
            )}

            {devices.map((d, idx) => {
                const online = !!(d.lastSeenMs && (Date.now() - d.lastSeenMs) / 60000 <= ONLINE_MIN);
                const pendingCmd = d.deviceId ? pendingLogouts[d.deviceId] : null;   // 대기 중 로그아웃 명령
                const sent = d.deviceId && (sentIds.includes(d.deviceId) || !!pendingCmd);
                // [목록에서 제거] 노출 — 7일 이상 신호 없음 또는 구버전 (켜진 PC는 제외)
                const staleDays = d.lastSeenMs ? (Date.now() - d.lastSeenMs) / 86400000 : Infinity;
                const removable = !!d.deviceId && !online && (staleDays >= STALE_DAYS || isOldVersion(d.version));
                const stmtPaths = Array.isArray(d.stmtFolderPaths) ? d.stmtFolderPaths : [];
                const cadPaths = Array.isArray(d.cadFolderPaths) ? d.cadFolderPaths : [];
                const stmtMore = (d.stmtFolderCount || 0) - stmtPaths.length;
                const cadMore = (d.cadFolderCount || 0) - cadPaths.length;
                // 수집 폴더 미지정 경고 — 배열이 있고 길이 0일 때만 (구버전은 미보고라 판정 제외, utils/agentFolders.js)
                const noStmtFolder = isMissingStmtFolder(d);
                const isApproved = d.deviceId && approvedIds.includes(d.deviceId);
                const isPending = d.deviceId && d.pendingApproval && !isApproved;
                // 하트비트 진단 정보 (alpha.38 계약 확정: devices.{id}.diag = { slowFolder, cycleInterval(ms), lastError })
                const diag = (d.diag && typeof d.diag === 'object') ? d.diag : null;
                const slowMin = diag?.slowFolder ? cycleMinutes(diag.cycleInterval) : null;
                // 최근 활동 로그 = priv/logs 문서의 devices.{id}.logs [{at,m}] (마스킹됨, 회사 전용)
                const rawLogs = d.deviceId ? privLogs?.devices?.[d.deviceId]?.logs : null;
                const recentLogs = Array.isArray(rawLogs) ? rawLogs.filter(l => l && typeof l.m === 'string' && l.m) : null;
                const logOpen = d.deviceId && logOpenIds.includes(d.deviceId);
                return (
                    <div key={d.deviceId || `legacy-${idx}`} style={S.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15 }}>{online ? '🟢' : '⚪'}</span>
                            <span style={{ fontSize: 13, fontWeight: 800, color: online ? '#15803D' : '#64748b' }}>
                                {online ? '켜짐' : `꺼짐 (마지막 활동 ${agoText(d.lastSeenMs)})`}
                            </span>
                            <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{displayName(d)}</span>
                            {deviceNames[d.deviceId] && d.deviceName && (
                                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>기기명 {d.deviceName}</span>
                            )}
                            {d.deviceId && (
                                <button onClick={() => renameDevice(d)} style={S.renameBtn} title="표시 이름 바꾸기">
                                    <Pencil size={12} />
                                    이름 바꾸기
                                </button>
                            )}
                            {d.edition && <span style={S.chip}>{EDITION_LABEL[d.edition] || d.edition}</span>}
                            {d.version && <span style={S.chip}>v{d.version}</span>}
                            {d.platform && <span style={S.chip}>{PLATFORM_LABEL[d.platform] || d.platform}</span>}
                            {noStmtFolder && <span style={S.warnBadge}>수집 폴더 없음</span>}
                            {diag?.slowFolder && (
                                <span style={S.slowBadge} title="연결 폴더 응답이 느려 감지 주기를 자동으로 늘렸습니다 (NAS·네트워크 폴더 등)">
                                    {'⚠ 연결 폴더가 느립니다'}{slowMin ? `(감지 주기 ${slowMin}분으로 조정됨)` : '(감지 주기 자동 조정됨)'}
                                </span>
                            )}
                            {isPending && <span style={S.pendingBadge}>승인 대기</span>}
                            {approvalMode && isApproved && <span style={S.approvedBadge}>승인됨</span>}
                            {pendingCmd && (
                                <span style={S.reservedBadge} title="이 PC가 켜지면 로그아웃됩니다">
                                    {'로그아웃 예약됨'}{pendingCmd.createdAtMs ? ` · ${logTimeText(pendingCmd.createdAtMs)}` : ''}
                                </span>
                            )}
                            <span style={{ flex: 1 }} />
                            {isPending && (
                                <button
                                    onClick={() => approveDevice(d)}
                                    disabled={approving === d.deviceId}
                                    style={{ ...S.approveBtn, ...(approving === d.deviceId ? { opacity: 0.6 } : {}) }}
                                >
                                    <ShieldCheck size={14} />
                                    {approving === d.deviceId ? '승인 중…' : '승인'}
                                </button>
                            )}
                            {approvalMode && isApproved && (
                                <button
                                    onClick={() => unapproveDevice(d)}
                                    disabled={approving === d.deviceId}
                                    style={{ ...S.unapproveBtn, ...(approving === d.deviceId ? { opacity: 0.6 } : {}) }}
                                >
                                    {approving === d.deviceId ? '처리 중…' : '승인 해제'}
                                </button>
                            )}
                            {d.deviceId && (
                                // [설정 열기] — 이 PC의 덴클 프로그램 설정 화면을 원격으로 연다 (open_settings, AgentDevices는 그 PC 지목)
                                <OpenAgentSettingsButton
                                    labId={labId}
                                    section="cad"
                                    targetDeviceId={d.deviceId}
                                    requestedBy={user?.uid}
                                    label="설정 열기"
                                />
                            )}
                            {d.deviceId ? (
                                <button
                                    onClick={() => sendLogout(d, !online)}
                                    disabled={!!sending || sent}
                                    style={{ ...S.logoutBtn, ...(sent ? S.logoutBtnDone : {}), ...(sending === d.deviceId ? { opacity: 0.6 } : {}) }}
                                >
                                    <LogOut size={14} />
                                    {sent
                                        ? (online ? '로그아웃 명령 보냄' : '로그아웃 예약됨')
                                        : (sending === d.deviceId ? '보내는 중…' : (online ? '이 PC 로그아웃' : '켜지면 로그아웃(예약)'))}
                                </button>
                            ) : (
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>구버전 프로그램 — 원격 로그아웃은 업데이트 후 지원</span>
                            )}
                            {pendingCmd && (
                                <button
                                    onClick={() => cancelLogout(d, pendingCmd)}
                                    disabled={cancelling === pendingCmd.id}
                                    style={{ ...S.unapproveBtn, ...(cancelling === pendingCmd.id ? { opacity: 0.6 } : {}) }}
                                >
                                    {cancelling === pendingCmd.id ? '취소 중…' : '예약 취소'}
                                </button>
                            )}
                            {removable && (
                                <button
                                    onClick={() => removeDevice(d)}
                                    disabled={removing === d.deviceId}
                                    style={{ ...S.unapproveBtn, ...(removing === d.deviceId ? { opacity: 0.6 } : {}) }}
                                    title="목록 정리만 합니다 — 이 PC의 로그인·승인 상태는 그대로입니다"
                                >
                                    <Trash2 size={13} />
                                    {removing === d.deviceId ? '지우는 중…' : '목록에서 제거'}
                                </button>
                            )}
                        </div>
                        <div style={S.metaRow}>
                            <span>마지막 활동: <b style={{ color: online ? '#15803D' : '#64748b' }}>{agoText(d.lastSeenMs)}</b></span>
                            {d.lastCollectMs && <span>· 마지막 수집: {agoText(d.lastCollectMs)}</span>}
                        </div>
                        {noStmtFolder && (
                            <div style={S.warnBox}>
                                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>
                                    <b>{MISSING_STMT_TITLE}</b>
                                    <div style={{ marginTop: 3, fontWeight: 500 }}>{MISSING_STMT_HELP}</div>
                                </span>
                            </div>
                        )}
                        {(stmtPaths.length > 0 || cadPaths.length > 0 || d.homeWorkRecvRoot !== undefined || d.homeShareRoot !== undefined || d.outsourceRecvRoot !== undefined) && (
                            <div style={S.folderBox}>
                                {stmtPaths.length > 0 && (
                                    <div style={S.folderLine}>
                                        <FolderOpen size={13} color="#059669" style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span><b style={{ color: '#059669' }}>수집 폴더</b> {stmtPaths.join(' · ')}{stmtMore > 0 ? ` 외 ${stmtMore}개` : ''}</span>
                                    </div>
                                )}
                                {cadPaths.length > 0 && (
                                    <div style={S.folderLine}>
                                        <FolderOpen size={13} color="#6366f1" style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span><b style={{ color: '#6366f1' }}>CAD 폴더</b> {cadPaths.join(' · ')}{cadMore > 0 ? ` 외 ${cadMore}개` : ''}</span>
                                    </div>
                                )}
                                {/* 재택·외주 폴더 (2026-08-31 대표 승인) — 하트비트 필드가 있는 신버전 PC만 표시.
                                    ''(빈 값)=지정 안 함 → 재택 받는 폴더만 기본 폴더 문구, 나머지는 '미지정' */}
                                {d.homeWorkRecvRoot !== undefined && (
                                    <div style={S.folderLine}>
                                        <FolderOpen size={13} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span><b style={{ color: '#d97706' }}>재택 받는 폴더</b> {d.homeWorkRecvRoot || '미지정 — 기본 폴더(문서\\DentCluster\\재택작업)'}</span>
                                    </div>
                                )}
                                {d.homeShareRoot !== undefined && d.homeShareRoot && (
                                    <div style={S.folderLine}>
                                        <FolderOpen size={13} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span><b style={{ color: '#d97706' }}>재택 공유 폴더</b> {d.homeShareRoot}</span>
                                    </div>
                                )}
                                {d.outsourceRecvRoot !== undefined && d.outsourceRecvRoot && (
                                    <div style={S.folderLine}>
                                        <FolderOpen size={13} color="#0e7490" style={{ flexShrink: 0, marginTop: 2 }} />
                                        <span><b style={{ color: '#0e7490' }}>외주 받는 폴더</b> {d.outsourceRecvRoot}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* 「최근 활동」 접기 — priv/logs 문서(최근 8건, 마스킹됨, 회사 전용) 표시 전용 (다리 3종 ②).
                            제작 현황의 '덴클 꺼진 PC' 배너와 역할 중복 없음: 여기는 로그 나열만. */}
                        {d.deviceId && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                                <button
                                    onClick={() => setLogOpenIds(prev => logOpen ? prev.filter(id => id !== d.deviceId) : [...prev, d.deviceId])}
                                    style={S.logToggle}
                                >
                                    {logOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                    최근 활동
                                </button>
                                {logOpen && (
                                    <div style={S.logBox}>
                                        {diag?.lastError && (
                                            <div style={{ ...S.logLine, color: '#B91C1C', fontWeight: 700 }}>{diag.lastError}</div>
                                        )}
                                        {recentLogs && recentLogs.length > 0 ? (
                                            recentLogs.map((l, i) => (
                                                <div key={i} style={S.logLine}>
                                                    {logTimeText(l.at) && <span style={{ color: '#94a3b8', marginRight: 6 }}>{logTimeText(l.at)}</span>}
                                                    {l.m}
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ ...S.logLine, color: '#94a3b8' }}>
                                                {diag?.lastError ? '다른 활동 기록이 없습니다.' : '이 버전은 활동 로그를 지원하지 않습니다 — 덴클 프로그램 업데이트가 필요합니다.'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {devices.length > 0 && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <RefreshCw size={12} />
                    상태는 각 PC가 5분마다 보고합니다 — 🟢 켜짐 = 최근 {ONLINE_MIN}분 안에 신호 있음. 꺼진 PC에 보낸 로그아웃은 예약으로 남아 그 PC가 켜질 때 적용됩니다.
                    {STALE_DAYS}일 이상 신호가 없거나 구버전인 항목은 [목록에서 제거]로 정리할 수 있습니다(PC 자격은 그대로).
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: 760, margin: '0 auto', padding: '0 16px 40px' },
    desc: { fontSize: 13.5, color: '#64748b', lineHeight: 1.7, marginTop: 4, marginBottom: 16 },
    notice: {
        background: '#EDFBF1', border: '1px solid #BBF7D0', color: '#15803D',
        borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12, lineHeight: 1.6,
    },
    emptyBox: {
        background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12,
        padding: '32px 20px', textAlign: 'center', fontSize: 13.5, color: '#64748b',
    },
    card: {
        background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '14px 16px', marginBottom: 10,
    },
    chip: {
        fontSize: 11.5, fontWeight: 700, color: '#475569', background: '#f1f5f9',
        border: '1px solid #e2e8f0', borderRadius: 6, padding: '1px 8px',
    },
    metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 12.5, color: '#64748b', marginTop: 8 },
    folderBox: { marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 4 },
    folderLine: { display: 'flex', gap: 6, fontSize: 12, color: '#475569', lineHeight: 1.5, wordBreak: 'break-all' },
    logoutBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: '#fff', border: '1px solid #FECACA', color: '#DC2626',
        borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
    },
    logoutBtnDone: { border: '1px solid #e2e8f0', color: '#94a3b8', cursor: 'default', background: '#f8fafc' },
    renameBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#fff', border: '1px solid #e2e8f0', color: '#475569',
        borderRadius: 7, padding: '3px 9px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
    },
    // ⚠️ 수집 폴더 미지정 경고 (2026-08-09) — 기존 경고색(pendingBanner) 계열보다 강한 적색 사용
    warnBox: {
        display: 'flex', gap: 8, alignItems: 'flex-start',
        background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
        borderRadius: 10, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.6, marginTop: 8,
    },
    warnBadge: {
        fontSize: 11.5, fontWeight: 800, color: '#B91C1C', background: '#FEF2F2',
        border: '1px solid #FECACA', borderRadius: 6, padding: '1px 8px',
    },
    // ⚠️ 느린 폴더 배지 (다리 3종 ③) — 경고이되 오류는 아니라 호박색 계열 (pendingBadge 계열)
    slowBadge: {
        fontSize: 11.5, fontWeight: 800, color: '#B45309', background: '#FFFBEB',
        border: '1px solid #FDE68A', borderRadius: 6, padding: '1px 8px',
    },
    // 「최근 활동」 접기 (다리 3종 ②)
    logToggle: {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: 'none', border: 'none', padding: 0,
        fontSize: 12, fontWeight: 700, color: '#64748b', cursor: 'pointer',
    },
    logBox: {
        marginTop: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3,
    },
    logLine: { fontSize: 12, color: '#475569', lineHeight: 1.5, wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
    // 🖥️ 신규 PC 승인제 (다중 PC 2단계)
    pendingBanner: {
        display: 'flex', gap: 8, alignItems: 'flex-start',
        background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E',
        borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 12, lineHeight: 1.6,
    },
    countLine: { fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.6 },
    pendingBadge: {
        fontSize: 11.5, fontWeight: 800, color: '#B45309', background: '#FEF3C7',
        border: '1px solid #FDE68A', borderRadius: 6, padding: '1px 8px',
    },
    // ⏳ 로그아웃 예약됨 배지 (2026-09-06) — 대기 중 remote_logout 명령 표시
    reservedBadge: {
        fontSize: 11.5, fontWeight: 800, color: '#B91C1C', background: '#FEF2F2',
        border: '1px solid #FECACA', borderRadius: 6, padding: '1px 8px',
    },
    approvedBadge: {
        fontSize: 11.5, fontWeight: 800, color: '#15803D', background: '#EDFBF1',
        border: '1px solid #BBF7D0', borderRadius: 6, padding: '1px 8px',
    },
    approveBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: '#059669', border: '1px solid #059669', color: '#fff',
        borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
    },
    unapproveBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: '#fff', border: '1px solid #e2e8f0', color: '#64748b',
        borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
    },
};

export default AgentDevices;
